import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormSection from 'cl-form/FormSections/FormSection';
import FormSectionsBase from 'cl-form/FormSections/FormSectionsBase';
import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import * as _ from 'lodash';
import BasicPage, {SubPage} from 'cl-layout/BasicPage';
import GeneralActions from '../../../shared/ActionsManager/GeneralActions/GeneralActions';
import {GENERAL_ACTIONS_TYPES} from '../../../shared/ActionsManager/ActionsTypes';
import {ACTION_DISPLAY_TYPES} from '../../../shared/ActionsManager/ActionsManagerConsts';
import EmptyState from '../../ViewTemplates/containers/EmptyState';
import {FormField} from 'cl-form/FormFields';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input/Input';
import Typography from '@material-ui/core/Typography/Typography';
import InlineButton from '../../../shared/components/InlineButton';
import {PlanAndBilling} from '../AccountPageSectionsData';
import {getAccountsConnected, getAccountsLimit} from '../../../Layout/MyAccountLayout/ApplicationLayoutSelectors';
import {spaceToDashed} from 'cl-automation-utils';
import {addDynamicDialog} from '../../../crazyEditor/redux/modules/panel/panel';
import EnableInventorySyncDialog, {
  EnableInventorySyncDialogProps,
} from '../../../shared/components/Dialogs/EnableInventorySyncDialog/EnableInventorySyncDialog';
import {Channels, productApi} from 'cl-api';
import {updateChannelAccountQuantitySync} from '../../ViewDesigns/redux/modules/accountPage';
import {refreshIntercom} from '../../../shared/helpers/analyticsHelper';
import createStyles from '@material-ui/core/styles/createStyles';
import {StoreState} from '../../../App/initialStore';
import {WrappedFieldMetaProps, WrappedFieldInputProps} from 'redux-form';
import FormSwitch from 'cl-form/FormSwitch';
import Tooltip from '@material-ui/core/Tooltip';

interface Account {
  id: string;
  name?: string;
  tokenExpirationDate?: string;
  quantitySyncEnabled?: boolean;
  listingsPageHref: string;
}

export interface ChannelAccountsProps {
  channel: Channels;
  onUpdateAccountName?: (any) => any;
  accounts?: Account[];
}

const unixToDateTime = unixTime => {
  const expirationDateInt = parseInt(unixTime, 10);
  return expirationDateInt ? new Date(1000 * expirationDateInt).toLocaleDateString() : 'N/A';
};

const useStyles = makeStyles(theme => {
  return createStyles({
    accountDetails: {
      margin: theme.spacing(2),
    },
    accountLimit: {
      marginTop: theme.spacing(2),
    },
    sellerIdDetails: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    bullets: {
      listStylePosition: 'outside',
    },
  });
});

const ChannelAccounts: React.FunctionComponent<ChannelAccountsProps> = ({
  channel,
  accounts,
  onUpdateAccountName = null,
}) => {
  const classes = useStyles();
  const accountsLimit = useSelector(state => getAccountsLimit(state));
  const accountsConnected = useSelector(state => getAccountsConnected(state));
  const inventorySyncIsPaid = useSelector((state: StoreState) => state.user.isOnTrial || state.user.inventorySync);
  const dispatch = useDispatch();

  const onUpdateQuantitySyncSwitch = async (quantitySyncEnabled: boolean, sellerId: string) => {
    const {success} = await productApi.updateSellerQuantitySyncEnabled({
      sellerId,
      quantitySyncEnabled,
      channel,
    });
    if (success) {
      dispatch(updateChannelAccountQuantitySync(sellerId, quantitySyncEnabled, channel));
    }

    _.defer(() => {
      window['analytics'].track(`Inventory Sync ${quantitySyncEnabled ? 'Enabled' : 'Disabled'}`, {
        Channel: channel,
        sellerId: sellerId,
      });
      refreshIntercom();
    });
  };

  return (
    <BasicPage>
      <SubPage
        title={`${channel} Accounts`}
        contentWidth="md"
        actions={{
          topLeft: (
            <GeneralActions
              id={`cla-btn-${spaceToDashed(channel)}`}
              types={[GENERAL_ACTIONS_TYPES.OPEN_ADD_CHANNEL_ACCOUNT_DIALOG]}
              displayType={ACTION_DISPLAY_TYPES.RAISED_BUTTON}
              additionalActionData={{channel}}
            />
          ),
        }}
      >
        {_.isEmpty(accounts) ? (
          <EmptyState
            src="monsterActive"
            headline={`No ${channel} accounts Connected`}
            paragraphs={[`click “ADD ACCOUNT” to connect a new account.`]}
          />
        ) : (
          <>
            <div key={'accountLimit'} className={classes.accountLimit} id={`cla-btn-${spaceToDashed(channel)}-limit`}>
              You have connected {accountsConnected} out of {accountsLimit} accounts allowed with your plan. <br />
              <InlineButton id={`cla-btn-${spaceToDashed(channel)}-changeplan`} href={PlanAndBilling.route}>
                Change plan
              </InlineButton>{' '}
              to allow more {channel} accounts
            </div>
            <FormSectionsBase initialState={{openMode: 'single', openSectionKey: '0'}}>
              {_.map(
                accounts,
                ({name, id, tokenExpirationDate, quantitySyncEnabled, listingsPageHref}: Account, accountKey) => {
                  return (
                    <FormSection
                      key={accountKey}
                      title={name || id}
                      id={`cla-${spaceToDashed(channel)}-account-${accountKey}`}
                      stickyTitle={false}
                    >
                      <div className={classes.accountDetails}>
                        <Typography variant="h6">Account</Typography>
                        {name ? (
                          <FormField
                            fieldName="cla-field-alias"
                            touched={false}
                            showErrors={false}
                            error={false}
                            explanationText="This is how this account will be called in CrazyLister"
                          >
                            <TextField
                              label="Account alias (optional)"
                              defaultValue={name}
                              onBlur={onUpdateAccountName ? onUpdateAccountName(id) : undefined}
                            />
                          </FormField>
                        ) : (
                          undefined
                        )}
                        <div className={classes.sellerIdDetails}>
                          <FormControl>
                            <InputLabel>{`${channel} seller id`}</InputLabel>
                            <Input
                              className={`cla-input-${spaceToDashed(channel)}-seller-id`}
                              defaultValue={id}
                              disableUnderline
                              inputProps={{readOnly: true}}
                            />
                          </FormControl>
                          <Tooltip title="View and edit this account's listings">
                            <Button variant="outlined" href={listingsPageHref}>
                              manage listings
                              <ChevronRightIcon />
                            </Button>
                          </Tooltip>
                          <GeneralActions
                            types={[GENERAL_ACTIONS_TYPES.OPEN_DISCONNECT_CHANNEL_ACCOUNT_DIALOG]}
                            displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
                            additionalActionData={{
                              channel,
                              sellerId: id,
                              quantitySyncEnabled,
                              className: classes.bullets,
                            }}
                          />
                        </div>
                        {tokenExpirationDate ? (
                          <div className={classes.sellerIdDetails}>
                            <FormControl>
                              <InputLabel>Token expiration date</InputLabel>
                              <Input
                                className={`cla-input-${spaceToDashed(channel)}-token-exp-date`}
                                defaultValue={unixToDateTime(tokenExpirationDate)}
                                disableUnderline
                                inputProps={{readOnly: true}}
                              />
                            </FormControl>
                            <GeneralActions
                              types={[GENERAL_ACTIONS_TYPES.RENEW_EBAY_TOKEN]}
                              displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
                            />
                          </div>
                        ) : (
                          undefined
                        )}
                        <Typography variant="h6">Inventory</Typography>
                        <FormSwitch
                          input={{} as WrappedFieldInputProps}
                          meta={{touched: false, error: false} as WrappedFieldMetaProps}
                          touched={false}
                          error={false}
                          showErrors={false}
                          explanationText="Automatically update your listing quantities to match inventory in CrazyLister"
                          label={<>Inventory sync</>}
                          labelPlacement="start"
                          checked={quantitySyncEnabled}
                          onChange={(event, checked) =>
                            checked
                              ? dispatch(
                                  addDynamicDialog<EnableInventorySyncDialogProps>(EnableInventorySyncDialog, {
                                    channel,
                                    sellerId: id,
                                    enableInventorySyncFunction: onUpdateQuantitySyncSwitch,
                                    inventorySyncIsPaid,
                                  })
                                )
                              : onUpdateQuantitySyncSwitch(checked, id)
                          }
                        />
                      </div>
                    </FormSection>
                  );
                }
              )}
            </FormSectionsBase>
          </>
        )}
      </SubPage>
    </BasicPage>
  );
};

export default ChannelAccounts;
